import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import { RISK_COMPLIANCE_BLOCK_STATUS } from '../../dataHandlers/riskComplianceConstants';
import { getRiskLevelColors } from './riskComplianceStyles';
import DnBDataCard, { DnBFieldRow } from './DnBDataCard';

function RiskBadge({ riskLevel, label }) {
  const theme = useTheme();
  const colors = getRiskLevelColors(theme)[riskLevel] ?? getRiskLevelColors(theme).MODERATE;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.dot, flexShrink: 0 }} />
      <Typography
        variant="body2"
        fontWeight={theme.typography.fontWeightBold}
        sx={{ color: colors.text, letterSpacing: '0.04em', textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function formatScoreRow(score, scoreLabel) {
  if (!score?.classScore && !score?.description) {
    return undefined;
  }

  const parts = [];
  if (score.classScore) {
    parts.push(`${scoreLabel}: ${score.classScore}/10`);
  }
  if (score.scoreDate) {
    parts.push(`(${score.scoreDate})`);
  }
  if (score.description) {
    parts.push(score.description);
  }

  return parts.join(' · ');
}

export default function DnBRiskRatingCard({
  block,
  labels,
  emptyMessage,
  unavailableMessage,
  noDataMessage,
  fetchLabel,
  onFetch,
  onRefresh,
  isFetching,
}) {
  const theme = useTheme();
  const data = block?.data;
  const riskLabel = labels.riskLevels?.[data?.riskLevel] ?? data?.riskLevel;

  return (
    <DnBDataCard
      title={labels.title}
      icon={<SpeedOutlinedIcon fontSize="small" color="action" />}
      blockStatus={block?.status || RISK_COMPLIANCE_BLOCK_STATUS.EMPTY}
      emptyMessage={emptyMessage}
      unavailableMessage={unavailableMessage}
      noDataMessage={noDataMessage}
      fetchLabel={fetchLabel}
      onFetch={onFetch}
      onRefresh={onRefresh}
      isFetching={isFetching}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          pb: 1.5,
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <RiskBadge riskLevel={data?.riskLevel} label={riskLabel} />
          {data?.financialCondition && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {labels.financialCondition}: {data.financialCondition}
            </Typography>
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, fontWeight: theme.typography.fontWeightMedium }}
        >
          {labels.rating} {data?.rating}
        </Typography>
      </Box>

      <DnBFieldRow label={labels.financialStrength} value={data?.financialStrength} />
      <DnBFieldRow label={labels.ratingScoreDate} value={data?.ratingScoreDate} />
      <DnBFieldRow label={labels.failureScore} value={formatScoreRow(data?.failureScore, labels.scoreClass)} />
      <DnBFieldRow label={labels.delinquencyScore} value={formatScoreRow(data?.delinquencyScore, labels.scoreClass)} />
      <DnBFieldRow label={labels.summary} value={data?.summary} />
    </DnBDataCard>
  );
}
